import React, { useEffect, useState, useCallback } from 'react';
import { FormControl, InputLabel, Select, MenuItem, CircularProgress, styled, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SiteDTO } from '@beable/roster-backend-client-v1';
import provisionService from '../../services/ProvisionService';
import { useCustomerDispatch, useCustomerState, CustomerActionType } from '../../context/CustomerContext';
import { getSiteSelectorStyles } from './styles';

interface SiteSelectorProps {
  siteId?: number;
  customerId: string;
  onSelect: any;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  allOption?: boolean;
  variant?: 'filled' | 'outlined';
  ignoreState?: boolean;
  inPanel?: boolean;
}

export default function SiteSelector({ siteId, onSelect, customerId, className, disabled, required, allOption, variant, ignoreState, inPanel }: SiteSelectorProps) {
  const theme = useTheme();
  const styles = getSiteSelectorStyles(theme);
  const customerDispatch = useCustomerDispatch();
  let customerState = useCustomerState();


  if (ignoreState) {
    customerState = {
      customer: undefined,
      site: undefined
    };
  }

  const [sites, setSites] = useState<SiteDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SiteDTO | undefined>(undefined);
  const [initialLoad, setInitialLoad] = useState(true);

  const memoOnSelect = useCallback(onSelect, []);

  useEffect(() => {
    const fetchSites = async () => {
      setSelectedSite(undefined);

      if (!customerId) {
        setIsLoading(false);
        memoOnSelect(undefined);
        return;
      }

      const data = await provisionService.getSitesForCustomer(customerId);
      if (data) {
        setSites(data);
        setIsLoading(false);

        let site;
        if (data.length === 1) {
          site = data[0];
        }

        if (siteId) {
          site = data.find(s => s.id === siteId);
        }

        setSelectedSite(site);
        memoOnSelect(site);
        setInitialLoad(false);
      } else {
        setIsLoading(false);
        window.alert("Could not fetch the sites for this customer")
      }
    }

    setIsLoading(true);
    fetchSites();
  }, [memoOnSelect, customerId, siteId]);

  useEffect(() => {
    if (!initialLoad && !selectedSite && sites.length > 1 && customerState.site) {
      const site = sites.find((s) => s.id === customerState.site?.id);
      if (site) {
        setSelectedSite(site);
        memoOnSelect(site)
      }
    }
  }, [initialLoad, memoOnSelect, sites, customerState, selectedSite]);

  const handleSiteChange = async (event: any) => {
    setIsLoading(true);
    const sitesUpdated = await provisionService.getSitesForCustomer(customerId);
    if (sitesUpdated) {
      setSites(sitesUpdated);
      setIsLoading(false);
      const selectedSite = sitesUpdated.find((site) => site.id === Number(event.target.value));
      setSelectedSite(selectedSite);
      if (onSelect) {
        onSelect(selectedSite);
      }

      customerDispatch({
        type: CustomerActionType.SiteChanged,
        site: selectedSite
      });
    }
  };

  const getLabel = () => {
    if (customerId && !sites.length)
      return "No Sites Found";

    return "Select Site" + (required ? '*' : '');
  }

  return <>
    {isLoading && (
      <Box sx={styles.loaderContainer}>
        <CircularProgress disableShrink />
      </Box>
    )}
    {!isLoading && (
      <FormControl fullWidth className={className} sx={inPanel ? styles.selectListPanel : (className ? undefined : styles.selectList)} variant={variant || "standard"}>
        <InputLabel id='siteSelectorLabel'>{getLabel()}</InputLabel>
        <Select
          variant={variant || "standard"}
          labelId="siteSelectorLabel"
          data-testid="siteSelector"
          data-cy="site-select"
          value={selectedSite?.id?.toString() ?? ''}
          onChange={handleSiteChange}
          disabled={sites.length < 2 || disabled}>
          {allOption && sites.length &&
            <MenuItem value='ALL' role='option'>
              All Sites
            </MenuItem>
          }
          {sites.map((site: any) => (
            <MenuItem key={site.id} value={site.id}>
              {site.subdomain}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )}
  </>;
}

export const ModalSiteSelector = styled(SiteSelector)(({ theme }) => getSiteSelectorStyles(theme).modalSelector);